import { c as _c } from "react/compiler-runtime";
import { PreservingNavigate } from "@components/common/preserve-navigation/preserving-navigate";
import { LoadingScreen } from "@flat-ui";
import { NotFoundPage } from "@pages/not-found-page";
import { AccountSettingsTabs, MainRoutes, MemberTabs, ProjectTabs } from "@router/route-params";
import { generateAccountSettingsRoute, generateMemberProfileRoute, generateProjectDetailsRoute, generateProjectsRoute, generateTeamsRoute, useAppParams } from "@router/router-helper";
import { selectedProjectIdSelector } from "@store/projects/projects-selector";
import { useAppSelector } from "@store/store-helper";
import { fetchingUserFlagsSelector } from "@store/user/user-selector";
import { matchPath, useLocation } from "react-router-dom";

// Another page with an instant redirect can be found at .../pages/data-management/data-management.tsx
// It has its own implementation since it needs to call trackOpenProject before redirecting, which isn't
// possible with the PreservingNavigate element used here.

/** List of all routes of HDB that are deprecated and not planned to implement in SDB */
const deprecatedFeatureRoutes = ["/settings/templates/categories", "/settings/addOns-and-services"];

/**
 * Manages redirection of the unknown routes.
 */
export function RedirectUnknownRoutes() {
  const $ = _c(17);
  const location = useLocation();
  const {
    companyId
  } = useAppParams();
  const projectId = useAppSelector(selectedProjectIdSelector);
  const {
    isFetchingCurrentUser
  } = useAppSelector(fetchingUserFlagsSelector);
  const currentPath = location.pathname;
  const currentPathWithoutCompanyId = currentPath.replace(`/${companyId}`, "");
  const normalizedCurrentPath = currentPathWithoutCompanyId.endsWith("/") ? currentPathWithoutCompanyId.slice(0, -1) : currentPathWithoutCompanyId;
  if (!companyId || isFetchingCurrentUser) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <LoadingScreen />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (normalizedCurrentPath === "/me") {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <PreservingNavigate to={generateAccountSettingsRoute()} />;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (companyId && normalizedCurrentPath === "/settings/integrations") {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <PreservingNavigate to={generateAccountSettingsRoute(AccountSettingsTabs.integrations)} />;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  if (companyId && normalizedCurrentPath === `/${MainRoutes.teams}`) {
    let t0;
    if ($[3] !== companyId) {
      t0 = generateTeamsRoute(companyId);
      $[3] = companyId;
      $[4] = t0;
    } else {
      t0 = $[4];
    }
    let t1;
    if ($[5] !== t0) {
      t1 = <PreservingNavigate to={t0} />;
      $[5] = t0;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  if (companyId && projectId && normalizedCurrentPath.includes(`/${MainRoutes.projects}`) && normalizedCurrentPath.endsWith("/team")) {
    let t0;
    if ($[7] !== companyId || $[8] !== projectId) {
      t0 = generateProjectDetailsRoute(companyId, projectId, ProjectTabs.members);
      $[7] = companyId;
      $[8] = projectId;
      $[9] = t0;
    } else {
      t0 = $[9];
    }
    let t1;
    if ($[10] !== t0) {
      t1 = <PreservingNavigate to={t0} />;
      $[10] = t0;
      $[11] = t1;
    } else {
      t1 = $[11];
    }
    return t1;
  }
  const legacyMemberProfileMatch = matchPath(`/${MainRoutes.members}/:memberId/${MemberTabs.overview}`, normalizedCurrentPath);
  if (legacyMemberProfileMatch?.params.memberId) {
    return <PreservingNavigate to={generateMemberProfileRoute(companyId, legacyMemberProfileMatch.params.memberId)} replace={true} />;
  }
  if (companyId && deprecatedFeatureRoutes.includes(normalizedCurrentPath)) {
    let t0;
    if ($[12] !== companyId) {
      t0 = generateProjectsRoute(companyId);
      $[12] = companyId;
      $[13] = t0;
    } else {
      t0 = $[13];
    }
    let t1;
    if ($[14] !== t0) {
      t1 = <PreservingNavigate to={t0} replace={true} />;
      $[14] = t0;
      $[15] = t1;
    } else {
      t1 = $[15];
    }
    return t1;
  }
  let t0;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <NotFoundPage />;
    $[16] = t0;
  } else {
    t0 = $[16];
  }
  return t0;
}